import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import api from '../utils/api';
import { refreshCurrentMarketIfNeeded } from '../utils/marketLocation';
import { getNewsEdition, refreshNewsEdition, trackNewsV2Event } from '../api/newsV2';
import homeStyles from '../components/home/HomeHub.module.css';
import { HomeSidebar, HomeTopBar } from '../components/home/HomeHubParts';
import NotificationsDrawer from '../components/home/NotificationsDrawer';
import useHubNotifications from '../hooks/useHubNotifications';
import UpgradePrompt from '../components/entitlements/UpgradePrompt';
import { newsSignalTags } from '../utils/insightPreferences';
import '../styles/InsightNewsPage.css';

const POLL_MS = 4000;
const POLL_MAX = 15;
const MANUAL_OUTCOME_CHIP_MS = 15 * 60 * 1000;
/** Preview row counts — full arrays stay in API; list view slices only */
const PREVIEW_MASTER_DEVS = 3;
const PREVIEW_INDUSTRY_DEVS = 2;

function recentManualRefreshOutcome(meta) {
  if (!meta?.lastRefreshCompletedAt) return false;
  return Date.now() - new Date(meta.lastRefreshCompletedAt).getTime() < MANUAL_OUTCOME_CHIP_MS;
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Newline-separated watch lines from model output */
function splitWatchLines(text) {
  if (!text || typeof text !== 'string') return [];
  return text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
}

function buildBriefingShareText(briefing) {
  if (!briefing) return '';
  const lines = [briefing.veloxHeadline, briefing.dek].filter(Boolean);
  return lines.join('\n\n').trim();
}

function BriefingSynthesis({ briefing, afterBody }) {
  if (!briefing) return null;
  const why = briefing.whyItMatters && String(briefing.whyItMatters).trim();
  const watch = splitWatchLines(briefing.whatToWatch);
  if (!why && watch.length === 0) return null;
  return (
    <div className={afterBody ? 'news-v2-synth news-v2-synth--after-body' : 'news-v2-synth'}>
      {why ? (
        <>
          <p className="news-v2-synth-label">Why it matters</p>
          <p className="news-v2-why-matters">{why}</p>
        </>
      ) : null}
      {watch.length > 0 ? (
        <>
          <p className="news-v2-synth-label">What to watch</p>
          <ul className="news-v2-what-to-watch">
            {watch.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function hasDevelopmentsUx(briefing) {
  return Array.isArray(briefing?.developments) && briefing.developments.length > 0;
}

/** Mixed mode: master is strategic prose only; key feed is top-level keyDevelopments */
function isMixedKeyDevelopmentsMode(edition) {
  return edition?.metadata?.displayMode === 'master_plus_key_developments';
}

function masterBriefingUsesBodyUx(edition) {
  if (isMixedKeyDevelopmentsMode(edition)) return true;
  return !hasDevelopmentsUx(edition?.masterBriefing);
}

/** Dedupe by URL: briefing-level sources plus per-development sources (detail modal). */
function mergeBriefingSourcesForDetail(briefing) {
  if (!briefing) return [];
  const seen = new Set();
  const out = [];
  const push = (arr) => {
    for (const s of arr || []) {
      const u = s && s.url ? String(s.url) : '';
      if (!u || seen.has(u)) continue;
      seen.add(u);
      out.push(s);
    }
  };
  push(briefing.sources);
  for (const d of briefing.developments || []) {
    push(d.sources);
  }
  return out;
}

function DevelopmentsPreview({ developments, limit }) {
  const list = (developments || []).slice(0, limit);
  const rest = Math.max(0, (developments || []).length - list.length);
  if (list.length === 0) return null;
  return (
    <div className="news-v2-dev-preview-wrap" aria-label="Key developments">
      <p className="news-v2-dev-preview-label">Key developments</p>
      <ul className="news-v2-dev-preview">
        {list.map((d) => (
          <li key={d.id || d.title}>
            <span className="news-v2-dev-preview-title">{d.title}</span>
            {d.dek ? <span className="news-v2-dev-preview-dek">{d.dek}</span> : null}
          </li>
        ))}
      </ul>
      {rest > 0 ? (
        <p className="news-v2-dev-preview-more">
          +{rest} more in detail
        </p>
      ) : null}
    </div>
  );
}

function DevelopmentsDetail({ developments, trackContext }) {
  const list = developments || [];
  if (list.length === 0) return null;
  const byId = new Map(list.map((d) => [d.id, d]));
  return (
    <div className="news-v2-dev-detail-section">
      <p className="news-v2-synth-label">Key developments</p>
      {list.map((d) => (
        <div key={d.id || d.title} id={d.id ? `news-v2-dev-${d.id}` : undefined} className="news-v2-dev-detail">
          <h4 className="news-v2-dev-detail-title">{d.title}</h4>
          <p className="news-v2-dev-detail-analysis">{(d.detailAnalysis && String(d.detailAnalysis).trim()) || d.dek || ''}</p>
          {d.entities && d.entities.length > 0 ? (
            <p className="news-v2-dev-entities">{d.entities.join(' · ')}</p>
          ) : null}
          {d.relatedDevelopmentIds && d.relatedDevelopmentIds.length > 0 ? (
            <p className="news-v2-dev-related">
              Related:{' '}
              {d.relatedDevelopmentIds.map((rid, i) => {
                const other = byId.get(rid);
                if (!other) return null;
                return (
                  <span key={rid}>
                    {i > 0 ? ' · ' : ''}
                    <a
                      href={`#news-v2-dev-${rid}`}
                      className="news-v2-dev-related-link"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(`news-v2-dev-${rid}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }}
                    >
                      {other.title}
                    </a>
                  </span>
                );
              })}
            </p>
          ) : null}
          {d.sources && d.sources.length > 0 ? (
            <ul className="news-v2-dev-sources-mini">
              {d.sources.map((s, idx) => (
                <li key={idx}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackContext &&
                      trackNewsV2Event({
                        event: 'source_click',
                        targetType: trackContext.type,
                        targetId: trackContext.briefingId,
                        sectionId: `dev:${d.id || idx}`,
                        sourceArticleId: s.sourceArticleId || undefined
                      })
                    }
                  >
                    {s.title || s.url}
                  </a>
                  {s.outlet ? <span className="news-v2-source-meta"> — {s.outlet}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
    </div>
  );
}

const InsightNewsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const notifications = useHubNotifications();
  const { showError, showSuccess, showWarning } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [edition, setEdition] = useState(null);
  const [editionStatus, setEditionStatus] = useState(null);
  const [locked, setLocked] = useState(false);
  const [userPreferences, setUserPreferences] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [meta, setMeta] = useState(null);
  const pollRef = useRef(0);
  const awaitingRefreshOutcome = useRef(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [showRefreshQuota, setShowRefreshQuota] = useState(false);

  const loadEdition = useCallback(async () => {
    const data = await getNewsEdition();
    // On a free plan the server sends a headline and blurb instead of the
    // briefing, so there is no body here to hide — there is none to show.
    setLocked(data.locked === true);
    setEdition(data.edition);
    setEditionStatus(data.status || data.edition?.status);
    setMeta({
      lastCorpusRefreshAt: data.lastCorpusRefreshAt,
      isStale: data.isStale,
      refreshPending: data.refreshPending,
      quota: data.quota,
      emptyReason: data.emptyReason,
      editionAgeMs: data.editionAgeMs,
      retrievalSummary: data.retrievalSummary || null,
      preferencesAligned: data.preferencesAligned,
      preferencesOutOfSync: data.preferencesOutOfSync,
      lastRefreshOutcome: data.lastRefreshOutcome,
      lastRefreshCompletedAt: data.lastRefreshCompletedAt,
      refreshUxStatus: data.refreshUxStatus,
      refreshTransparencyNote: data.refreshTransparencyNote
    });
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const preferencesResponse = await api.get('/api/user/insight-preferences');
        if (cancelled) return;
        const prefs = preferencesResponse.data.preferences;
        setUserPreferences(prefs);
        await refreshCurrentMarketIfNeeded({ preferences: prefs, force: false });
        if (cancelled) return;
        await loadEdition();
      } catch (error) {
        console.error('Error loading news edition:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadEdition]);

  useEffect(() => {
    const pending =
      !locked &&
      (meta?.refreshPending === true ||
        editionStatus === 'generating' ||
        edition?.status === 'generating');
    if (!pending) {
      pollRef.current = 0;
      setPollTimedOut(false);
      return undefined;
    }
    const t = setInterval(async () => {
      pollRef.current += 1;
      if (pollRef.current > POLL_MAX) {
        // Generation is still pending after the max polling window. Stop polling and
        // surface an honest "still preparing" state instead of an infinite spinner.
        // We do NOT fake success — the backend edition remains pending.
        clearInterval(t);
        awaitingRefreshOutcome.current = false;
        setPollTimedOut(true);
        showWarning('Still preparing your briefing — try again shortly.');
        return;
      }
      try {
        const data = await getNewsEdition();
        setLocked(data.locked === true);
        setEdition(data.edition);
        setEditionStatus(data.status || data.edition?.status);
        setMeta({
          lastCorpusRefreshAt: data.lastCorpusRefreshAt,
          isStale: data.isStale,
          refreshPending: data.refreshPending,
          quota: data.quota,
          emptyReason: data.emptyReason,
          editionAgeMs: data.editionAgeMs,
          retrievalSummary: data.retrievalSummary || null,
          preferencesAligned: data.preferencesAligned,
          preferencesOutOfSync: data.preferencesOutOfSync,
          lastRefreshOutcome: data.lastRefreshOutcome,
          lastRefreshCompletedAt: data.lastRefreshCompletedAt,
          refreshUxStatus: data.refreshUxStatus,
          refreshTransparencyNote: data.refreshTransparencyNote
        });
        const st = data.status || data.edition?.status;
        if (!data.refreshPending && (st === 'complete' || st === 'failed' || st === 'insufficient_sources')) {
          clearInterval(t);
        }
      } catch {
        /* ignore */
      }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [locked, editionStatus, edition?.status, meta?.refreshPending, showWarning]);

  useEffect(() => {
    if (!awaitingRefreshOutcome.current) return;
    if (meta?.refreshPending) return;
    const st = editionStatus || edition?.status;
    if (st !== 'complete' && st !== 'insufficient_sources' && st !== 'failed') return;
    awaitingRefreshOutcome.current = false;
    const ux = meta?.refreshUxStatus;
    if (ux === 'updated') {
      showSuccess('Briefing updated.');
    } else if (ux === 'minor') {
      showSuccess('No major changes since your last briefing.');
    } else if (ux === 'unchanged_prior') {
      showWarning(
        edition?.errorMessage ||
          'We couldn’t replace your briefing with a new run. Your previous edition is unchanged.'
      );
    } else if (ux === 'insufficient') {
      showWarning(edition?.errorMessage || 'Not enough matching stories for a full briefing right now.');
    }
    // showSuccess/showWarning come from ToastContext and are useCallback-wrapped
    // with stable deps, so listing them cannot cause this effect to re-run.
  }, [meta?.refreshPending, meta?.refreshUxStatus, editionStatus, edition?.status, edition?.errorMessage, showSuccess, showWarning]);

  const handleRefreshNews = async () => {
    setIsGenerating(true);
    setShowRefreshQuota(true);
    trackNewsV2Event({ event: 'refresh_click', targetType: 'news_page', targetId: 'refresh' });
    try {
      const res = await refreshNewsEdition();
      if (res.accepted) {
        showSuccess(res.deduped ? res.message || 'Already updating.' : res.message || 'Refreshing your briefing…');
        awaitingRefreshOutcome.current = true;
        setEditionStatus('generating');
        setMeta((m) => ({ ...m, refreshPending: true }));
        pollRef.current = 0;
        setPollTimedOut(false);
        await loadEdition();
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      if (error.response?.status === 402) {
        setLocked(true);
        showWarning(msg || 'Refreshing your briefing is part of Pro.');
      } else if (error.response?.status === 429) {
        showWarning(msg || 'Refresh limit reached for today.');
      } else if (error.response?.status === 400) {
        showWarning(msg || 'Update preferences to enable your briefing.');
      } else {
        showError('Could not start refresh');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const openDetail = (section) => {
    setExpandedSection(section);
    trackNewsV2Event({
      event: 'briefing_open',
      targetType: section.type,
      targetId: section.id
    });
  };

  const handleShareBriefing = async (briefing) => {
    const text = buildBriefingShareText(briefing);
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const payload = { title: 'Velox briefing', text: text || 'Velox briefing', url };
    trackNewsV2Event({ event: 'briefing_share', targetType: 'briefing', targetId: briefing?.id || 'briefing' });
    try {
      if (navigator.share && typeof navigator.share === 'function') {
        await navigator.share(payload);
        return;
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`.trim());
      showSuccess('Link and summary copied');
    } catch {
      showError('Could not copy');
    }
  };

  const pageContent = isLoading ? (
    <div className="insight-news-page loading">
      <div className="loading-spinner" />
      <p>Loading your briefing…</p>
    </div>
  ) : (
    <div className="insight-news-page news-v2">
      {locked ? null : (
      <header className="news-v2-page-header">
        <h1 className="news-v2-page-title">Today’s briefing</h1>
        <div className="news-v2-status-strip" role="status">
          <button
            type="button"
            className="refresh-button news-v2-refresh-primary"
            onClick={handleRefreshNews}
            disabled={isGenerating}
          >
            {isGenerating ? 'Queueing…' : 'Refresh briefing'}
          </button>
          {edition?.generatedAt ? (
            <span className="news-v2-status-item">Updated {formatTime(edition.generatedAt)}</span>
          ) : (
            <span className="news-v2-status-item muted">—</span>
          )}
          {showRefreshQuota && meta?.quota ? (
            <span className="news-v2-status-item">
              Refreshes: {meta.quota.remaining}/{meta.quota.max}
            </span>
          ) : null}
          {meta?.refreshPending && !pollTimedOut ? (
            <span className="news-v2-status-chip news-v2-status-chip--info">Refreshing your briefing…</span>
          ) : null}
          {meta?.refreshPending && pollTimedOut ? (
            <span className="news-v2-status-chip news-v2-status-chip--soft">Still preparing — try again shortly</span>
          ) : null}
          {!meta?.refreshPending &&
          recentManualRefreshOutcome(meta) &&
          meta?.refreshUxStatus === 'updated' ? (
            <span className="news-v2-status-chip news-v2-status-chip--success">Briefing updated</span>
          ) : null}
          {!meta?.refreshPending &&
          recentManualRefreshOutcome(meta) &&
          meta?.refreshUxStatus === 'minor' ? (
            <span className="news-v2-status-chip news-v2-status-chip--soft">No major changes</span>
          ) : null}
          {meta?.isStale && !meta?.refreshPending ? (
            <span className="news-v2-status-chip news-v2-status-chip--soft">Stale</span>
          ) : null}
          {edition?.metadata?.fallbackTier === 'f2_synthetic' ? (
            <span className="news-v2-status-chip news-v2-status-chip--soft" title="Synthetic fallback">
              Strategic view
            </span>
          ) : null}
        </div>
        {meta?.retrievalSummary ? (
          <p className="news-v2-retrieval-line">{meta.retrievalSummary}</p>
        ) : null}
      </header>
      )}

      {!locked && meta?.preferencesOutOfSync ? (
        <div className="news-v2-banner news-v2-banner--info" role="status">
          Your Insight Preferences changed after this briefing was generated. Refresh to align the briefing with your
          current selections.
        </div>
      ) : null}

      {!locked && meta?.refreshPending && edition?.masterBriefing?.veloxHeadline ? (
        <div className="news-v2-banner news-v2-banner--info" role="status">
          Updating your briefing — your previous edition stays below until the new one is ready.
        </div>
      ) : null}

      {!locked && meta?.isStale && !meta?.refreshPending && edition?.masterBriefing?.veloxHeadline ? (
        <div className="news-v2-banner news-v2-banner--soft" role="status">
          This edition is more than a day old. Refresh when you want a new synthesis.
        </div>
      ) : null}

      {!locked && edition?.errorMessage && edition?.status === 'complete' ? (
        <div className="news-v2-banner news-v2-banner--warn" role="alert">
          {edition.errorMessage}
        </div>
      ) : null}

      {!locked && edition?.metadata?.generationNote === 'thin_corpus_refresh_skipped' && !meta?.refreshPending ? (
        <div className="news-v2-banner news-v2-banner--soft" role="status">
          Not enough new matching stories for a full refresh — your previous briefing is unchanged.
        </div>
      ) : null}

      {!locked && edition?.metadata?.generationNote === 'strict_relevance_insufficient' && !meta?.refreshPending ? (
        <div className="news-v2-banner news-v2-banner--soft" role="status">
          {edition?.errorMessage ||
            'Not enough articles clearly matched your current preferences. Try again later or broaden your selections.'}
        </div>
      ) : null}

      {!locked && editionStatus === 'failed' && !edition?.masterBriefing?.veloxHeadline ? (
        <div className="no-news">
          <h3>Briefing unavailable</h3>
          <p>{edition?.errorMessage || 'Generation failed. Try refresh later.'}</p>
        </div>
      ) : null}

      {!locked && editionStatus === 'insufficient_sources' && !edition?.masterBriefing?.veloxHeadline ? (
        <div className="no-news">
          <h3>Not enough matching stories yet</h3>
          <p>
            {edition?.errorMessage ||
              'Broaden your industries or interests, or try again after more headlines publish.'}
          </p>
        </div>
      ) : null}

      {locked ? (
        <div className="news-v2-upgrade">
          <UpgradePrompt
            feature="insights_full"
            title="Unlock your market intelligence"
            body="Follow the developments relevant to your industries and interests. Explore market analysis and industry trends tailored to your interests. Pro also gives you a daily briefing of what matters most."
            ctaLabel="Explore Pro"
            ctaTo="/upgrade?focus=pro"
            showTierLine={false}
            preview={edition?.preview?.headline ? edition.preview : null}
            unlockedBy={edition?.unlockedBy}
          />
        </div>
      ) : null}

      {!locked &&
      (meta?.refreshPending === true ||
        editionStatus === 'generating' ||
        edition?.status === 'generating') &&
      !edition?.masterBriefing?.veloxHeadline ? (
        <div className="no-news">
          <h3>Preparing your edition</h3>
          {pollTimedOut ? (
            <p>
              Still preparing your briefing — this is taking longer than usual. You can leave this page and
              check back shortly, or tap “Refresh briefing” to try again.
            </p>
          ) : (
            <p>We’re assembling stories that match your preferences. You can leave this page — we’ll keep working in the background.</p>
          )}
        </div>
      ) : null}

      {!locked && edition?.masterBriefing?.veloxHeadline ? (
        <div className="news-content news-v2-briefings">
          <section className="news-v2-master">
            <h2 className="news-v2-section-title">Master briefing</h2>
            <article className="news-card news-v2-card">
              <h3 className="news-title">{edition.masterBriefing.veloxHeadline}</h3>
              {edition.masterBriefing.dek ? <p className="news-summary">{edition.masterBriefing.dek}</p> : null}
              {masterBriefingUsesBodyUx(edition) || !hasDevelopmentsUx(edition.masterBriefing) ? (
                <>
                  {(edition.masterBriefing.bodySections || []).map((sec, i) => (
                    <div key={i} className="news-v2-body-block">
                      {sec.heading ? <h4 className="news-v2-subhead">{sec.heading}</h4> : null}
                      <p className="news-v2-body-text">{sec.text}</p>
                    </div>
                  ))}
                  <BriefingSynthesis briefing={edition.masterBriefing} afterBody />
                </>
              ) : (
                <DevelopmentsPreview
                  developments={edition.masterBriefing.developments}
                  limit={PREVIEW_MASTER_DEVS}
                />
              )}
              <button type="button" className="expand-button" onClick={() => openDetail({ type: 'master', id: edition.masterBriefing.id, briefing: edition.masterBriefing })}>
                Sources & detail
              </button>
            </article>
          </section>

          {isMixedKeyDevelopmentsMode(edition) ? (
            <section className="news-v2-key-developments news-v2-key-developments--primary" aria-labelledby="key-developments-heading">
              <h2 id="key-developments-heading" className="news-v2-section-title">
                Key developments
              </h2>
              {Array.isArray(edition.keyDevelopments) && edition.keyDevelopments.length > 0 ? (
                <article className="news-card news-v2-card news-v2-key-dev-card">
                  <DevelopmentsPreview developments={edition.keyDevelopments} limit={48} />
                  <button
                    type="button"
                    className="expand-button"
                    onClick={() =>
                      openDetail({
                        type: 'key_developments',
                        id: 'key_developments_feed',
                        industry: 'Key developments',
                        briefing: {
                          id: 'key_developments_feed',
                          veloxHeadline: 'Key developments',
                          dek: '',
                          bodySections: [],
                          developments: edition.keyDevelopments,
                          sources: []
                        }
                      })
                    }
                  >
                    Sources & detail
                  </button>
                </article>
              ) : (
                <p className="news-edition-meta">No discrete developments in this run — sector context below still applies.</p>
              )}
            </section>
          ) : null}

          {Array.isArray(edition.industryBriefings) && edition.industryBriefings.length > 0 ? (
            <section
              className={
                isMixedKeyDevelopmentsMode(edition)
                  ? 'news-v2-industries news-v2-industries--secondary'
                  : 'news-v2-industries'
              }
            >
              {isMixedKeyDevelopmentsMode(edition) ? (
                <details className="news-v2-industries-collapsed">
                  <summary className="news-v2-section-title news-v2-section-title--summary">Sector context</summary>
                  <div className="news-grid">
                    {edition.industryBriefings.map((row) => (
                      <article key={row.industry} className="news-card news-v2-card news-v2-card--slim">
                        <span className="news-category">{row.industry}</span>
                        <h3 className="news-title">{row.briefing.veloxHeadline}</h3>
                        {row.briefing.dek ? <p className="news-summary">{row.briefing.dek}</p> : null}
                        <button
                          type="button"
                          className="expand-button"
                          onClick={() =>
                            openDetail({
                              type: 'industry',
                              id: row.briefing.id,
                              industry: row.industry,
                              briefing: row.briefing
                            })
                          }
                        >
                          Sources & detail
                        </button>
                      </article>
                    ))}
                  </div>
                </details>
              ) : (
                <>
                  <h2 className="news-v2-section-title">By industry</h2>
                  <div className="news-grid">
                    {edition.industryBriefings.map((row) => (
                      <article key={row.industry} className="news-card news-v2-card">
                        <span className="news-category">{row.industry}</span>
                        <h3 className="news-title">{row.briefing.veloxHeadline}</h3>
                        {row.briefing.dek ? <p className="news-summary">{row.briefing.dek}</p> : null}
                        {hasDevelopmentsUx(row.briefing) ? (
                          <DevelopmentsPreview developments={row.briefing.developments} limit={PREVIEW_INDUSTRY_DEVS} />
                        ) : (
                          <>
                            {(row.briefing.bodySections || []).slice(0, 2).map((sec, i) => (
                              <div key={i} className="news-v2-body-block">
                                {sec.heading ? <h4 className="news-v2-subhead">{sec.heading}</h4> : null}
                                <p className="news-v2-body-text">{sec.text}</p>
                              </div>
                            ))}
                            <BriefingSynthesis briefing={row.briefing} afterBody />
                          </>
                        )}
                        <button
                          type="button"
                          className="expand-button"
                          onClick={() =>
                            openDetail({
                              type: 'industry',
                              id: row.briefing.id,
                              industry: row.industry,
                              briefing: row.briefing
                            })
                          }
                        >
                          Sources & detail
                        </button>
                      </article>
                    ))}
                  </div>
                </>
              )}
            </section>
          ) : null}
        </div>
      ) : null}

      {!locked &&
      !edition?.masterBriefing?.veloxHeadline &&
      edition?.status !== 'failed' &&
      edition?.status !== 'insufficient_sources' &&
      edition?.status !== 'pending' &&
      edition?.status !== 'generating' ? (
        <div className="preferences-summary">
          {userPreferences ? (
            <p className="muted">
              Tip: add industries, technologies, companies, stages, additional interests, or location — then use Refresh.
            </p>
          ) : null}
        </div>
      ) : null}

      {userPreferences && (
        <div className="preferences-summary preferences-summary--footer">
          <h3 className="preferences-summary-title">Your signals</h3>
          {newsSignalTags(userPreferences).length > 0 ? (
            <div className="preference-tags">
              {newsSignalTags(userPreferences).map((tag) => (
                <span key={`${tag.kind}-${tag.label}`} className={`preference-tag ${tag.kind}`}>
                  {tag.label}
                </span>
              ))}
            </div>
          ) : null}
          <button type="button" className="update-preferences-link" onClick={() => navigate('/insight-preferences')}>
            Update preferences
          </button>
        </div>
      )}

      {expandedSection ? (
        <div className="news-modal-overlay" role="dialog" aria-modal="true" onClick={() => setExpandedSection(null)}>
          <div className="news-modal-content news-v2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="news-modal-header">
              <span className="news-modal-category">{expandedSection.industry || 'Briefing'}</span>
              <div className="news-modal-header-actions">
                <button
                  type="button"
                  className="news-modal-share"
                  onClick={() => handleShareBriefing(expandedSection.briefing)}
                >
                  Share
                </button>
                <button type="button" className="news-modal-close" onClick={() => setExpandedSection(null)} aria-label="Close">
                  ×
                </button>
              </div>
            </div>
            <div className="news-modal-body">
              <h2 className="news-modal-title">{expandedSection.briefing.veloxHeadline}</h2>
              {expandedSection.briefing.dek ? <p className="news-modal-dek">{expandedSection.briefing.dek}</p> : null}
              {hasDevelopmentsUx(expandedSection.briefing) ? (
                <BriefingSynthesis briefing={expandedSection.briefing} />
              ) : null}
              {hasDevelopmentsUx(expandedSection.briefing) ? (
                <DevelopmentsDetail
                  developments={expandedSection.briefing.developments}
                  trackContext={{
                    type: expandedSection.type,
                    briefingId: expandedSection.briefing.id
                  }}
                />
              ) : null}
              {(expandedSection.briefing.bodySections || []).map((sec, i) => (
                <div key={i} className="news-v2-body-block">
                  {sec.heading ? <h4>{sec.heading}</h4> : null}
                  <p className="summary-paragraph">{sec.text}</p>
                </div>
              ))}
              {!hasDevelopmentsUx(expandedSection.briefing) ? (
                <BriefingSynthesis briefing={expandedSection.briefing} afterBody />
              ) : null}
              {(() => {
                const merged = mergeBriefingSourcesForDetail(expandedSection.briefing);
                return merged.length > 0 ? (
                  <div className="related-links">
                    <h4 className="news-v2-sources-heading">Grounded in these reports</h4>
                    <ul className="news-v2-sources">
                      {merged.map((s, idx) => (
                        <li key={idx}>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              trackNewsV2Event({
                                event: 'source_click',
                                targetType: expandedSection.type,
                                targetId: expandedSection.briefing.id,
                                sectionId: String(idx),
                                sourceArticleId: s.sourceArticleId || undefined
                              })
                            }
                          >
                            {s.title || s.url}
                          </a>
                          <span className="news-v2-source-meta">
                            {s.outlet ? `${s.outlet}` : ''}
                            {s.publishedAt ? ` · ${formatTime(s.publishedAt)}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="muted">No source links stored for this section.</p>
                );
              })()}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={homeStyles.hubRoot}>
      <HomeSidebar
        open={isNavOpen}
        onClose={() => setIsNavOpen(false)}
        activePath={location.pathname}
        onNavigate={(to) => {
          setIsNavOpen(false);
          navigate(to);
        }}
      />
      <NotificationsDrawer
        open={notifications.open}
        onClose={notifications.close}
        {...notifications.drawerProps}
      />
      <div className={homeStyles.hubMain}>
        <HomeTopBar
          title="Market Intelligence"
          onMenu={() => setIsNavOpen(true)}
          notificationsBadge={notifications.badge}
          onNotifications={notifications.toggle}
        />
        <div className={homeStyles.hubContent}>{pageContent}</div>
      </div>
    </div>
  );
};

export default InsightNewsPage;
